import { getDateRange } from '../../actions/insights/utils';
import useInsights from '../../components/hooks/useInsights';
import { getGroupByTable } from '../../insights/reacttable-columns.js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type groupByTableFieldType = {
    accessor: string;
    Header: any;
    Cell: any;
    width?: number;
};
const useCompareInsights = ({
    filters,
    query: rawQuery,
    metric,
    client,
    enabled = true,
}: {
    filters: any;
    query: any;
    metric: string;
    client: any;
    enabled?: boolean;
}) => {
    const { t } = useTranslation('common');
    const groupByTable = useMemo(() => getGroupByTable(t), [t]);
    const isPlacementGroupBy = rawQuery?.groupBy?.includes(
        'rightsholderPlacement',
    );
    const isRightsholderGroupBy = rawQuery?.groupBy?.includes('rightsholder');
    const groupBy =
        isPlacementGroupBy && !isRightsholderGroupBy
            ? [...rawQuery.groupBy, 'rightsholder']
            : rawQuery.groupBy;
    const query = {
        ...rawQuery,
        groupBy,
    };

    const hasCompareDates = !!filters?.compareDates?.dates;
    const compareFilters = hasCompareDates
        ? {
              ...filters,
              startDate: filters.compareDates.dates.startDate,
              endDate: filters.compareDates.dates.endDate,
          }
        : null;

    const {
        data: report = { values: [], total: {} },
        isLoading: isLoadingReport,
    } = useInsights({
        filters,
        query,
        client,
        enabled,
    });

    const {
        data: compareReport = { values: [], total: {} },
        isLoading: isLoadingCompareReport,
    } = useInsights({
        filters: compareFilters,
        query,
        client,
        enabled: enabled && hasCompareDates,
    });

    const dateRange = getDateRange({
        startDate: filters.startDate,
        endDate: filters.endDate,
    });

    let compareDateRange = null;
    if (hasCompareDates) {
        compareDateRange = getDateRange({
            startDate: filters.compareDates.dates.startDate,
            endDate: filters.compareDates.dates.endDate,
        });
    }

    const accessors = useMemo(
        () =>
            query.groupBy.map(g => {
                const groupByTableOption: groupByTableFieldType | undefined =
                    groupByTable[g];
                return groupByTableOption?.accessor;
            }),
        [query],
    );

    const getKey = (v: any) => accessors.map(a => v[a]).join('');
    const normalize = vs => {
        return vs.reduce((acc, v) => {
            const key = getKey(v);
            if (!acc[key]) acc[key] = 0;
            acc[key] = v;
            return acc;
        }, {});
    };

    const data = useMemo(() => {
        if (!hasCompareDates || !report.total) return null;

        if (isLoadingReport || isLoadingCompareReport) return null;

        const { values: reportValues, total: reportTotal } = report;
        const { values: compareValues, total: compareTotal } = compareReport;

        const normalizedValues = normalize(
            reportValues.map(v => {
                return {
                    ...v,
                    dateRange,
                };
            }),
        );
        const normalizedCompareValues = normalize(
            compareValues.map(v => {
                return {
                    ...v,
                    dateRange: compareDateRange,
                };
            }),
        );

        const allKeys = [...reportValues, ...compareValues].map(getKey);
        const uniqueKeys = [...new Set(allKeys)];

        const values = uniqueKeys.map(k => {
            const isOriginal = !!normalizedValues[k];
            const value = normalizedValues[k] ? normalizedValues[k][metric] : 0;
            const compareValue = normalizedCompareValues[k]
                ? normalizedCompareValues[k][metric]
                : 0;

            const difference = value - compareValue;
            const percentageChange = compareValue
                ? difference / compareValue
                : 'N/A';
            return {
                ...(isOriginal
                    ? normalizedValues[k]
                    : normalizedCompareValues[k]),
                dateRange: value,
                compareDateRange: compareValue,
                difference,
                percentageChange,
            };
        });

        const total = {
            dateRange: reportTotal[metric],
            compareDateRange: compareTotal[metric],
            difference: reportTotal[metric] - compareTotal[metric],
            percentageChange: compareTotal[metric]
                ? (reportTotal[metric] - compareTotal[metric]) /
                  compareTotal[metric]
                : 'N/A',
        };

        return {
            values,
            total,
        };
    }, [
        isLoadingReport,
        isLoadingCompareReport,
        report,
        compareReport,
        hasCompareDates,
        metric,
        dateRange,
        compareDateRange,
    ]);

    return {
        data,
        isLoading: isLoadingReport || isLoadingCompareReport,
        dateRange,
        compareDateRange,
    };
};

export default useCompareInsights;
